import { useNavigate } from 'react-router-dom'

import { useStructureContext } from '../components/contexts/StructureContext'
import { useProjectContext } from '../components/contexts/ProjectContext'
import { useBoltConnectionContext } from '../components/contexts/BoltConnectionContext'
import BoltConnection from '../models/BoltConnection'
import DirectoryEntry from '../models/DirectoryEntry'
import Project from '../models/Project'
import { StructureType, StructureTypes } from '../models/Structure'
import { buildNewStructureLink } from '../tools/link'
import t from '../tools/t'

export const useCreateUnnamedRootEntryAndNavigate = () => {
    const navigate = useNavigate()
    const { createProject } = useStructureContext()
    return async () => {
        const newId = await createProject({ name: t('new_project_name') })
        if (!newId) return
        const link = buildNewStructureLink(StructureTypes.Project, newId)
        navigate(link)
    }
}

export const useCreateUnnamedEntryAndNavigate = () => {
    const navigate = useNavigate()
    const { createProject, createBoltConnection } = useStructureContext()
    return async (parent: DirectoryEntry | Project, type: StructureType) => {
        if (!parent) return
        const treeNodeLocation = { parentId: parent.id }

        let newId: string | undefined = undefined

        if (type === StructureTypes.Project) {
            newId = await createProject({
                name: t('new_project_name'),
                treeNodeLocation,
            })
        } else if (type === StructureTypes.BoltConnection) {
            newId = await createBoltConnection({
                name: t('new_bolt_connection_name'),
                treeNodeLocation,
            })
        }

        if (newId) {
            const link = buildNewStructureLink(type, newId)
            navigate(link)
        }
    }
}

export const useArchiveEntryAndNavigate = () => {
    const navigate = useNavigate()
    const { archiveProject, archiveBoltConnection } = useStructureContext()
    const { selectedProject } = useProjectContext()
    const { selectedBoltConnection } = useBoltConnectionContext()
    return async (entry: DirectoryEntry | Project | BoltConnection, type: StructureType) => {
        if (!confirm(`Are you sure you want to archive the ${type} "${entry.name}"?`)) return
        let success = undefined

        if (type === StructureTypes.Project) success = await archiveProject(entry.id)
        else if (type === StructureTypes.BoltConnection)
            success = await archiveBoltConnection(entry.id)

        // Do not perform navigation if the archive did not succeed.
        if (!success) return

        // Perform correct navigation to avoid remaining on an archived page and fetching a 404 error.
        if (entry.isRoot) navigate('/')
        else {
            // Check if the currently selected node is contained within the ancestry that has been archived.
            const selected = selectedProject ?? selectedBoltConnection
            const ancestors = selected?.ancestors ?? []
            let isSelectedArchived = selected?.id === entry.id
            for (const ancestor of ancestors) {
                if (ancestor.id === entry.id) {
                    isSelectedArchived = true
                    break
                }
                if (ancestor.id === selected?.id) break
            }

            if (isSelectedArchived) navigate(entry.parentLink)
        }
    }
}
